/**
 * InfoDot — React (web) implementation.
 *
 * A native `<button>` that is the visible disc. Its tappable area is larger
 * than the disc: an invisible `::before` reaches past each edge, so the button
 * takes up only the disc's layout room and sets no margin. Clicks stop
 * propagating so a dot inside a selectable card never also toggles the card's
 * own input.
 *
 * Fill, glyph colour and the hit area live in one injected stylesheet (not
 * inline styles) so hover, open and focus can all reach them through
 * selectors.
 */

import React from 'react';
import { Icon } from '../Icon/Icon.web';
import { ensureStyle, FOCUS_RING_OUTLINE } from '../../elements/focusStyle.web';
import {
  INFO_DOT_ACTIVE_BACKGROUND,
  INFO_DOT_ACTIVE_ICON_COLOR,
  INFO_DOT_DISC_SIZE,
  INFO_DOT_HIT_AREA_OUTSET,
  INFO_DOT_ICON,
  INFO_DOT_ICON_COLOR,
  INFO_DOT_ICON_SIZE,
  INFO_DOT_RADIUS,
  type InfoDotProps,
} from './InfoDot';

export interface WebInfoDotProps extends InfoDotProps {
  onToggle?: () => void;
  'data-testid'?: string;
}

const INFO_DOT_STYLE_ID = 'galapagos-info-dot';
const BUTTON = '[data-galapagos-info-dot="true"]';

function ensureInfoDotStyle(): void {
  ensureStyle(
    INFO_DOT_STYLE_ID,
    [
      `${BUTTON} { background-color: transparent; color: ${INFO_DOT_ICON_COLOR}; }`,
      // The invisible tappable area, centred on the disc.
      `${BUTTON}::before { content: ""; position: absolute; inset: -${INFO_DOT_HIT_AREA_OUTSET}px; border-radius: ${INFO_DOT_RADIUS}px; }`,
      `${BUTTON}[aria-expanded="true"] {` +
        ` background-color: ${INFO_DOT_ACTIVE_BACKGROUND}; color: ${INFO_DOT_ACTIVE_ICON_COLOR}; }`,
      // Hover only where a pointer can actually hover, so a tap on a touch
      // screen does not leave the dot looking open.
      `@media (hover: hover) { ${BUTTON}:hover {` +
        ` background-color: ${INFO_DOT_ACTIVE_BACKGROUND}; color: ${INFO_DOT_ACTIVE_ICON_COLOR}; } }`,
      `${BUTTON}:focus { outline: none; }`,
      `${BUTTON}:focus-visible { ${FOCUS_RING_OUTLINE} }`,
    ].join('\n'),
  );
}

const buttonStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: INFO_DOT_DISC_SIZE,
  height: INFO_DOT_DISC_SIZE,
  padding: 0,
  border: 'none',
  borderRadius: INFO_DOT_RADIUS,
  cursor: 'pointer',
};

export function InfoDot({
  accessibilityLabel,
  expanded = false,
  controls,
  testID,
  onToggle,
  actionId = 'ui.info-dot.toggle',
  'data-testid': dataTestId,
}: WebInfoDotProps) {
  ensureInfoDotStyle();

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    onToggle?.();
  };

  // Enter and Space activate the button through a synthesized click, but the
  // keydown itself still bubbles; stop it so a parent handling those keys
  // (a selectable card) does not also act.
  const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
  };

  return (
    <button
      type="button"
      aria-label={accessibilityLabel}
      aria-expanded={expanded}
      aria-controls={controls}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={buttonStyle}
      data-galapagos-info-dot="true"
      data-analytics-action={actionId}
      data-testid={dataTestId ?? testID}
    >
      <Icon name={INFO_DOT_ICON} size={INFO_DOT_ICON_SIZE} color="currentColor" aria-hidden />
    </button>
  );
}
